import type { FC } from "react";
import { useTranslation, Trans } from "react-i18next";

import { Typography, Box, BoxSection, Link, Button } from "@aviary";
import type { AviaryColors } from "@aviary/types/aviaryColors";
import { useBreakpoints } from "@shared/hooks";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/shared/locales/i18n";

import * as styles from "./SSOErrorBox.styles";

interface Props {
  isColor?: AviaryColors;
  errorMessage?: string;
  subErrorMessage?: string;
  hideSubErrorMessage?: boolean;
  onSignUpRedirect?: () => void;
  onSignInRedirect?: () => void;
}

const SSOErrorBox: FC<Props> = ({
  isColor,
  errorMessage,
  subErrorMessage,
  hideSubErrorMessage,
  onSignUpRedirect,
  onSignInRedirect,
}) => {
  const { t } = useTranslation();
  const { phoneLarge } = useBreakpoints();
  const typographySize = phoneLarge.greaterThan ? "body" : "footnote";

  const renderSignUpRedirectButton = onSignUpRedirect ? (
    <Link onClick={onSignUpRedirect}>{t(l.signIn.signUpRedirectCTA)}</Link>
  ) : (
    <Button noStyle to={authRoutes.practitioner_sign_up}>
      {t(l.signIn.signUpRedirectCTA)}
    </Button>
  );

  const renderSignInRedirectButton = onSignInRedirect ? (
    <Link onClick={onSignInRedirect}>{t(l.practitionerSignUp.signInRedirectCTA)}</Link>
  ) : (
    <Button noStyle to={authRoutes.practitioner_sign_up}>
      {t(l.practitionerSignUp.signInRedirectCTA)}
    </Button>
  );

  const renderErrorMessage = () => {
    if (errorMessage === null) {
      return (
        <Trans i18nKey={l.signIn.PleaseTryAnotherEmail}>
          Please try another email, or
          {renderSignUpRedirectButton}.
        </Trans>
      );
    } else if (errorMessage.includes("email exists")) {
      return (
        <Trans i18nKey={l.practitionerSignUp.AnAccountWithThisEmail}>
          An account with this email exists. Please {renderSignInRedirectButton}
        </Trans>
      );
    }

    return errorMessage;
  };

  const renderSubErrorMessage = () => {
    if (subErrorMessage === null) {
      return t(l.signIn.GmailNotConnected);
    }

    return subErrorMessage;
  };

  return (
    <Box isBordered isColor={isColor} css={styles.infoBox} data-testid="sso-error-box">
      <BoxSection isPaddingless>
        <Typography isFullWidth isSecondaryWeight={!hideSubErrorMessage} type={typographySize}>
          {renderErrorMessage()}
        </Typography>
        {!hideSubErrorMessage && (
          <Typography isFullWidth type={typographySize}>
            {renderSubErrorMessage()}
          </Typography>
        )}
      </BoxSection>
    </Box>
  );
};

SSOErrorBox.defaultProps = {
  isColor: "warning",
  errorMessage: null,
  subErrorMessage: null,
  hideSubErrorMessage: false,
};

export { SSOErrorBox };
